import { useMemo } from 'react';
import { MarketType } from '@velocity-exchange/sdk';
import useVelocityClient from './useVelocityClient';
import useCalculateTradeFee from './useCalculateTradeFee';
import { getIsZeroFeesActiveForMarket } from 'src/utils/zeroFees';
import Env from 'src/environmentVariables/EnvironmentVariables';

interface BaseMarketFeesProps {
	marketType: MarketType;
	marketIndex: number;
}

interface MarketFeesResult {
	takerFeePct: number;
	makerFeePct: number;
	isZeroFeesPromotion: boolean;
	actualTakerFeePct?: number; // For showing crossed out fees
	actualMakerFeePct?: number; // For showing crossed out fees
}

/**
 * Hook to get base market fees, falling back to direct SDK call when user-specific fees return 0%
 * This ensures market fee information is always available regardless of user connection status
 */
export const useBaseMarketFees = ({
	marketType,
	marketIndex,
}: BaseMarketFeesProps): MarketFeesResult => {
	const velocityClient = useVelocityClient();

	const marketFees = useCalculateTradeFee({
		quoteSize: 0,
		marketType,
		marketIndex,
	});

	const baseMarketFees = useMemo(() => {
		const isZeroFeesPromotion = getIsZeroFeesActiveForMarket({
			marketIndex,
			isPerp: marketType === MarketType.PERP,
		});

		// Get the actual market fees (either from hook or SDK)
		let actualTakerFeePct = marketFees.takerFeePct;
		let actualMakerFeePct = marketFees.makerFeePct;

		// The struck-through fee on a zero-fee market is what the user would
		// otherwise be charged, so it has to follow the live schedule (which a
		// promo tier can move). The env placeholders are only the fallback for
		// before it loads.
		if (isZeroFeesPromotion) {
			actualTakerFeePct =
				marketFees.takerFeePct || Env.defaultTakerFeeBps / 100;
			actualMakerFeePct =
				marketFees.makerFeePct || -Env.defaultMakerRebateBps / 100;

			return {
				takerFeePct: 0,
				makerFeePct: 0,
				isZeroFeesPromotion: true,
				actualTakerFeePct,
				actualMakerFeePct,
			};
		}

		// For normal markets, only get from SDK if hook returns 0%
		if (marketFees.takerFeePct === 0 && marketFees.makerFeePct === 0) {
			// Get base market fees from velocityClient without user account
			if (velocityClient?.isSubscribed) {
				try {
					const fees = velocityClient.getMarketFees(marketType, marketIndex);
					actualTakerFeePct = fees.takerFee * 100;
					actualMakerFeePct = fees.makerFee * 100;
				} catch (error) {
					console.warn('Failed to get base market fees:', error);
				}
			}
		}

		// For normal markets, return the actual fees
		return {
			takerFeePct: actualTakerFeePct,
			makerFeePct: actualMakerFeePct,
			isZeroFeesPromotion: false,
		};
	}, [marketFees, marketIndex, marketType, velocityClient]);

	return baseMarketFees;
};
